import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-luxon";
import { Line } from "react-chartjs-2";
import { LogoutButton } from "../Utility/AuthTokenRetrieve";
import "../Food/RecipeList.css";
import "./Scrapbook.css";
import {
  entryHasGrowth,
  fetchScrapbookEntries,
  formatMeasurement,
  getEntryCoverImage,
} from "./ScrapbookShared";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, TimeScale, Tooltip);

const categories = ["All", "Growth", "First", "Family", "Health", "Holiday", "Favorite", "Funny Moment"];

const buildChartData = (entries) => {
  const sorted = [...entries].sort((a, b) => a.entryDate.localeCompare(b.entryDate));
  return {
    datasets: [
      {
        label: "Weight",
        data: sorted.filter((entry) => entry.weightValue).map((entry) => ({ x: entry.entryDate, y: entry.weightValue })),
        borderColor: "#E55451",
        backgroundColor: "#E55451",
      },
      {
        label: "Length",
        data: sorted.filter((entry) => entry.lengthValue).map((entry) => ({ x: entry.entryDate, y: entry.lengthValue })),
        borderColor: "#5f7f95",
        backgroundColor: "#5f7f95",
      },
      {
        label: "Head",
        data: sorted.filter((entry) => entry.headCircumferenceValue).map((entry) => ({ x: entry.entryDate, y: entry.headCircumferenceValue })),
        borderColor: "#9b6b7a",
        backgroundColor: "#9b6b7a",
      },
    ],
  };
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: "time",
      time: { unit: "month" },
      grid: { display: false },
    },
    y: {
      beginAtZero: false,
      grid: { color: "rgba(0,0,0,.08)" },
    },
  },
};

const detailFields = [
  ["Weight", "weightValue", "weightUnit"],
  ["Length", "lengthValue", "lengthUnit"],
  ["Head circumference", "headCircumferenceValue", "headCircumferenceUnit"],
  ["Clothing size", "clothingSize"],
  ["Diaper size", "diaperSize"],
  ["Favorite toy", "favoriteToy"],
  ["Favorite song", "favoriteSong"],
  ["New words or sounds", "newWords"],
  ["New foods", "newFoods"],
];

const ScrapbookHome = () => {
  const [entries, setEntries] = useState([]);
  const [featuredEntry, setFeaturedEntry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    fetchScrapbookEntries()
      .then((data) => {
        setEntries(data.entries || []);
        setFeaturedEntry(data.featuredEntry || null);
        setLoading(false);
      })
      .catch(() => {
        setStatusMessage("Unable to load scrapbook entries.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedEntry) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedEntry(null);
      }
    };

    document.body.classList.add("ScrapbookModalOpen");
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("ScrapbookModalOpen");
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedEntry]);

  const filteredEntries = useMemo(() => (
    selectedCategory === "All"
      ? entries
      : entries.filter((entry) => entry.category === selectedCategory)
  ), [entries, selectedCategory]);

  const growthEntries = useMemo(() => entries.filter(entryHasGrowth), [entries]);
  const featuredImage = getEntryCoverImage(featuredEntry);

  return (
    <div className="ScrapbookPage">
      <div className="searchBarDiv formViewWrapper ScrapbookIntro">
        <div>
          <h2 className="ScrapbookTitle">Jon Scrapbook</h2>
          <p className="ScrapbookSubtitle">
            A private timeline for the pictures, measurements, firsts, and tiny details worth keeping.
          </p>
        </div>
        <div className="ScrapbookTopActions">
          <Link className="summaryButton ScrapbookAdminLink" to="/scrapbook/admin">Admin</Link>
          <LogoutButton />
        </div>
      </div>

      {featuredEntry ? (
        <div className="ScrapbookHero">
          {featuredImage ? <img src={featuredImage} alt={featuredEntry.title} /> : <div className="ScrapbookHeroPlaceholder">Jon Scrapbook</div>}
          <div className="ScrapbookHeroText">
            <span>{featuredEntry.ageLabel || featuredEntry.entryDateDisplay}</span>
            <h1>{featuredEntry.title}</h1>
            <p>{featuredEntry.story}</p>
          </div>
        </div>
      ) : null}

      {loading ? <div className="shoppingListDiv ScrapbookPanel">Loading scrapbook...</div> : null}
      {statusMessage ? <div className="shoppingListDiv ScrapbookPanel">{statusMessage}</div> : null}

      <div className="shoppingListDiv ScrapbookPanel">
        <div className="ScrapbookSectionHeader">
          <h3>Timeline</h3>
          <div className="ScrapbookFilterRow">
            {categories.map((category) => (
              <button
                className={category === selectedCategory ? "summaryButton ScrapbookFilterButton Active" : "summaryButton ScrapbookFilterButton"}
                key={category}
                onClick={() => setSelectedCategory(category)}
                type="button"
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="ScrapbookTimeline">
          {filteredEntries.length ? filteredEntries.map((entry) => {
            const coverImage = getEntryCoverImage(entry);
            return (
              <button
                aria-label={`View scrapbook entry ${entry.title}`}
                className="ScrapbookTimelineCard"
                key={entry.id}
                onClick={() => setSelectedEntry(entry)}
                type="button"
              >
                <div className="ScrapbookTimelineMarker" />
                {coverImage ? (
                  <img className="ScrapbookCardImage" src={coverImage} alt={entry.title} />
                ) : (
                  <div className="ScrapbookCardImage ScrapbookImageFallback">{entry.category}</div>
                )}
                <div className="ScrapbookCardBody">
                  <div className="ScrapbookCardMeta">
                    <span>{entry.category}</span>
                    <span>{entry.ageLabel || entry.entryDateDisplay}</span>
                  </div>
                  <h3>{entry.title}</h3>
                  {entry.story ? <p>{entry.story}</p> : null}
                  <div className="ScrapbookStats">
                    {entry.weightValue ? <span>Weight {formatMeasurement(entry.weightValue, entry.weightUnit)}</span> : null}
                    {entry.lengthValue ? <span>Length {formatMeasurement(entry.lengthValue, entry.lengthUnit)}</span> : null}
                    {entry.headCircumferenceValue ? <span>Head {formatMeasurement(entry.headCircumferenceValue, entry.headCircumferenceUnit)}</span> : null}
                    {entry.clothingSize ? <span>Clothes {entry.clothingSize}</span> : null}
                    {entry.diaperSize ? <span>Diapers {entry.diaperSize}</span> : null}
                  </div>
                  {entry.tags?.length ? (
                    <div className="ScrapbookTagRow">
                      {entry.tags.map((tag) => <span key={tag}>{tag}</span>)}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          }) : <p>No scrapbook entries yet.</p>}
        </div>
      </div>

      {growthEntries.length ? (
        <div className="shoppingListDiv ScrapbookPanel">
          <div className="ScrapbookSectionHeader">
            <h3>Growth</h3>
            <span>{growthEntries.length} logged measurements</span>
          </div>
          <div className="ScrapbookChart">
            <Line options={chartOptions} data={buildChartData(growthEntries)} />
          </div>
        </div>
      ) : null}

      {selectedEntry ? (
        <div
          aria-labelledby="ScrapbookModalTitle"
          aria-modal="true"
          className="ScrapbookModalOverlay"
          onClick={() => setSelectedEntry(null)}
          role="dialog"
        >
          <div className="ScrapbookModal" onClick={(event) => event.stopPropagation()}>
            <button
              aria-label="Close scrapbook entry"
              className="ScrapbookModalClose"
              onClick={() => setSelectedEntry(null)}
              type="button"
            >
              X
            </button>
            <div className="ScrapbookModalMedia">
              {selectedEntry.images?.length ? (
                <div className="ScrapbookModalImageGrid">
                  {selectedEntry.images.map((image) => (
                    <figure key={image.id}>
                      <img src={image.url} alt={image.caption || selectedEntry.title} />
                      {image.caption ? <figcaption>{image.caption}</figcaption> : null}
                    </figure>
                  ))}
                </div>
              ) : (
                <div className="ScrapbookModalImageFallback">{selectedEntry.category}</div>
              )}
            </div>
            <div className="ScrapbookModalDetails">
              <div className="ScrapbookCardMeta">
                <span>{selectedEntry.category}</span>
                <span>{selectedEntry.ageLabel || selectedEntry.entryDateDisplay}</span>
              </div>
              <h2 id="ScrapbookModalTitle">{selectedEntry.title}</h2>
              {selectedEntry.story ? <p>{selectedEntry.story}</p> : null}
              <div className="ScrapbookDetailGrid">
                {detailFields.map(([label, valueKey, unitKey]) => {
                  const value = selectedEntry[valueKey];
                  if (!value) return null;
                  return (
                    <div className="ScrapbookDetailItem" key={valueKey}>
                      <span>{label}</span>
                      <strong>{unitKey ? formatMeasurement(value, selectedEntry[unitKey]) : value}</strong>
                    </div>
                  );
                })}
              </div>
              {selectedEntry.healthNotes ? (
                <div className="ScrapbookModalNote">
                  <span>Health notes</span>
                  <p>{selectedEntry.healthNotes}</p>
                </div>
              ) : null}
              {selectedEntry.tags?.length ? (
                <div className="ScrapbookTagRow">
                  {selectedEntry.tags.map((tag) => <span key={tag}>{tag}</span>)}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ScrapbookHome;
